/**
 * NextStepsPanel.tsx
 *
 * W15 — AE account-level Next Steps panel.
 *
 * Shows the top 3 unanswered M-PACT checkpoints for a given account,
 * pulled from question_bank + account_cp_status.
 *
 * Logic:
 *   1. Load account_cp_status for this account — statuses per CP.
 *   2. Load question_bank for this tenant — one representative question per CP.
 *   3. Find CPs where status is NOT 'green' (not cleared) — these are unanswered.
 *   4. Order by sort_order ASC (question_bank.sort_order = priority proxy).
 *   5. Show top 3.
 *
 * View-only. No inline answer entry from this surface.
 *
 * Canon: No emojis, no exclamation points. Checkpoint not Gate. Tenant 1 only.
 * Palette: CANON_VISUALS_LOCKED 5-color scale.
 */

import { useEffect, useState } from "react";
import { supabase, getSession, restoreSession } from "@/lib/session";
import { CP_LABELS } from "@/lib/reporting-live";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NextStep {
  cpNumber: number;
  cpLabel: string;
  questionText: string;
  status: string | null;
  sortOrder: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TENANT_ID = 1; // Tenant 1 only per canon

function statusDot(status: string | null): string {
  switch (status) {
    case "green":   return "#16A34A";
    case "yellow":  return "#EAB308";
    case "orange":  return "#EA580C";
    case "red":     return "#DC2626";
    default:        return "#6B7280"; // gray = unscored/open
  }
}

function statusLabel(status: string | null): string {
  if (!status || status === "open") return "Not started";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Returns true if a CP is considered unanswered/incomplete. */
function isUnanswered(status: string | null): boolean {
  // 'green' = cleared. Everything else (null, 'open', 'red', 'orange', 'yellow') = needs work.
  return status !== "green";
}

async function getActiveTenantId(): Promise<number> {
  let s = getSession();
  if (!s) {
    await restoreSession();
    s = getSession();
  }
  return s?.tenantId ?? TENANT_ID;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface NextStepsPanelProps {
  accountId: number;
}

interface PanelState {
  loading: boolean;
  error: string | null;
  steps: NextStep[];
}

export default function NextStepsPanel({ accountId }: NextStepsPanelProps) {
  const [state, setState] = useState<PanelState>({
    loading: true,
    error: null,
    steps: [],
  });

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;

    (async () => {
      try {
        const tenantId = await getActiveTenantId();

        // 1. Load CP statuses for this account
        const { data: cpRows, error: cpErr } = await supabase()
          .from("account_cp_status")
          .select("cp_number,status")
          .eq("account_id", accountId);

        if (cpErr) throw new Error(`CP status load failed: ${cpErr.message}`);

        // Build a map: cp_number → status
        const cpStatusMap = new Map<number, string | null>();
        for (const row of (cpRows ?? []) as Array<{ cp_number: number; status: string | null }>) {
          cpStatusMap.set(row.cp_number, row.status);
        }

        // 2. Load question_bank — one row per gate_number (distinct by lowest sort_order)
        const { data: qbRows, error: qbErr } = await supabase()
          .from("question_bank")
          .select("id,gate_number,question_text,sort_order,is_active,tenant_id")
          .eq("tenant_id", tenantId)
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (qbErr) throw new Error(`Question bank load failed: ${qbErr.message}`);

        // 3. Deduplicate — keep first (lowest sort_order) question per CP gate
        const seenGates = new Set<number>();
        const candidates: NextStep[] = [];

        for (const q of (qbRows ?? []) as Array<{ id: string; gate_number: number; question_text: string; sort_order: number | null }>) {
          const cpNum = q.gate_number;
          if (seenGates.has(cpNum)) continue;
          seenGates.add(cpNum);

          const status = cpStatusMap.get(cpNum) ?? null;
          if (!isUnanswered(status)) continue; // skip cleared CPs

          candidates.push({
            cpNumber: cpNum,
            cpLabel: CP_LABELS[cpNum] ?? `CP${cpNum}`,
            questionText: q.question_text,
            status,
            sortOrder: q.sort_order ?? cpNum * 10,
          });
        }

        // 4. Also include CPs 1-10 with no question_bank entry if they are unanswered
        //    (covers sparse question_bank or CPs that are simply open with no questions yet)
        for (let n = 1; n <= 10; n++) {
          if (seenGates.has(n)) continue;
          const status = cpStatusMap.get(n) ?? null;
          if (!isUnanswered(status)) continue;
          candidates.push({
            cpNumber: n,
            cpLabel: CP_LABELS[n] ?? `CP${n}`,
            questionText: `Review and answer the ${CP_LABELS[n] ?? `CP${n}`} checkpoint questions.`,
            status,
            sortOrder: n * 10,
          });
        }

        // 5. Sort by sortOrder ASC, take top 3
        candidates.sort((a, b) => a.sortOrder - b.sortOrder);
        const top3 = candidates.slice(0, 3);

        if (!cancelled) {
          setState({ loading: false, error: null, steps: top3 });
        }
      } catch (err: any) {
        if (!cancelled) {
          setState({ loading: false, error: err?.message || "Failed to load next steps", steps: [] });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [accountId]);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: "16px 20px",
      }}
    >
      {/* Loading state */}
      {state.loading && (
        <div style={{ color: "#8E8E94", fontSize: 13, padding: "8px 0" }}>
          Loading next steps...
        </div>
      )}

      {/* Error state */}
      {!state.loading && state.error && (
        <div
          style={{
            background: "#FEF2F2",
            border: "1px solid #FCA5A5",
            borderRadius: 7,
            padding: "10px 12px",
            color: "#991B1B",
            fontSize: 13,
          }}
        >
          {state.error}
        </div>
      )}

      {/* Empty state */}
      {!state.loading && !state.error && state.steps.length === 0 && (
        <div
          style={{
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 7,
            padding: "10px 12px",
            color: "#166534",
            fontSize: 13,
          }}
        >
          All tracked checkpoints are cleared for this account.
        </div>
      )}

      {/* Next steps list */}
      {!state.loading && !state.error && state.steps.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {state.steps.map((step, idx) => (
            <div
              key={step.cpNumber}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "10px 12px",
                background: "#FAFAF7",
                border: "1px solid #E5E5E7",
                borderLeft: `4px solid ${statusDot(step.status)}`,
                borderRadius: 7,
              }}
            >
              {/* Priority number */}
              <div
                style={{
                  flexShrink: 0,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "#0B0B0F",
                  color: "#FFFFFF",
                  fontSize: 11,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 1,
                }}
              >
                {idx + 1}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* CP badge + status */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#525258",
                    }}
                  >
                    CP{step.cpNumber} — {step.cpLabel}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      color: statusDot(step.status),
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: statusDot(step.status),
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    {statusLabel(step.status)}
                  </span>
                </div>

                {/* Question text */}
                <div
                  style={{
                    fontSize: 13,
                    color: "#0B0B0F",
                    lineHeight: 1.5,
                    fontWeight: 500,
                  }}
                >
                  {step.questionText}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View-only notice */}
      {!state.loading && (
        <div
          style={{
            marginTop: 10,
            fontSize: 11,
            color: "#8E8E94",
            fontStyle: "italic",
          }}
        >
          Answer questions in the Discovery section below.
        </div>
      )}
    </div>
  );
}
